import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import classNames from 'classnames';

import { getCustomBetError, getRealOfferStatus } from '../utils';
import ConfirmAction from '../ConfirmAction';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import IconClose from '../../../../images/icons/misc/close.svg';
import IconCheck from '../../../../images/icons/misc/check.svg';
import IconSettings from '../../../../images/betslip/UI_settings.svg';
import IconMultiples from '../../../../images/icons/misc/icon-multiples.svg';

import BetslipSettings from '../BetslipSettings/BetslipSettings';

import styles from './Buttons.module.scss';

const NewButtons = ({
  onRemoveMultipleSelections,
  onRemoveOffers,
  onSubmitOffers,
  onCancelOffers,
  setSubmitAllWarning,
  setCancelAllWarning,
  config,
  showBetConfirmation,
  updateRightSidebar,
  offers,
  activeTab,
  multiplesAvailable,
  handleSettings,
  showSettings,
  setShowBetConfirmation,
  setAggregationRule,
  activeFilters,
  aggregationRuleStored
}) => {
  const agregationOptions = ['AVG', 'ALL', 'SUM'];
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);
  const [confirmSubmitAll, setConfirmSubmitAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agregationRule, setAgregationRule] = useState(aggregationRuleStored);

  const betsEligibleForSubmission = offers.filter((offer) => {
    const {
      processingState,
      backersStake,
      odds,
      error = []
    } = offer;

    const betBuilderBetContainErrors = getCustomBetError(error[0]);

    return (
      !['submitting', 'submitted'].includes(processingState)
      && !betBuilderBetContainErrors
      && backersStake
      && odds
    );
  });

  const submitAllBets = () => {
    setIsSubmitting(true);
    if (config.submitAllWarning) {
      setConfirmCancelAll(true);
      return;
    }

    onSubmitOffers(betsEligibleForSubmission);
  };

  const removeAllBets = () => {
    const offersToRemove = offers.filter(offer => offer.processingState !== 'submitting').map(offer => offer.cid);
    onRemoveMultipleSelections(offersToRemove);
    onRemoveOffers(offersToRemove);
  };

  const cancelAllBets = () => {
    if (config.cancelAllWarning) {
      setConfirmCancelAll(true);
      return;
    }

    const offersToCancel = offers.filter((offer) => {
      const { status, processingState } = getRealOfferStatus(offer);
      return status !== 'delayed' && ['open', 'edited'].includes(status) && processingState !== 'submitting';
    }).map(offer => offer.id);

    onCancelOffers(offersToCancel.toArray());
  };

  const convertSelections = () => {
    updateRightSidebar({
      content: 'multiples',
      visible: true
    });
  };

  const handleAgregationRules = () => {
    if (agregationOptions.indexOf(agregationRule) + 1 === agregationOptions.length) {
      setAgregationRule(agregationOptions[0]);
      setAggregationRule(agregationOptions[0]);
    } else {
      setAgregationRule(agregationOptions[agregationOptions.indexOf(agregationRule) + 1]);
      setAggregationRule(agregationOptions[agregationOptions.indexOf(agregationRule) + 1]);
    }
  };

  useEffect(() => {
    offers.forEach((offer) => {
      if (['submitting', 'submitted'].includes(offer.processingState)) {
        setIsSubmitting(true);
      } else if (['failed'].includes(offer.processingState)) {
        setIsSubmitting(false);
      } else {
        setIsSubmitting(false);
      }
    });
  }, [offers]);

  const reviewingOffer = offers.find(bet => bet.processingState === 'reviewing');

  const enabled = offers.some((offer) => {
    const { status, processingState } = getRealOfferStatus(offer);
    return status !== 'delayed' && processingState !== 'submitting';
  });

  const isCancelling = offers.some((offer) => {
    const { processingState } = getRealOfferStatus(offer);
    return processingState === 'cancelling';
  });

  const hasOfferSubmitted = offers.some((offer) => {
    const { processingState } = getRealOfferStatus(offer);
    return processingState === 'submitted';
  });

  const isUnmatched = offers.some((offer) => {
    const { status } = getRealOfferStatus(offer);
    return (status === 'open' || status === 'edited');
  });

  const submitAllDisabled = showBetConfirmation
    || isSubmitting
    || offers.size === 0
    || offers.size > 200
    || !betsEligibleForSubmission.size
    || offers.every(({
      backersStake,
      odds
    }) => !backersStake || !odds);

  const buttonClasses = classNames({
    [styles.buttonContainer]: true,
    [styles['buttonContainer--settingsOpen']]: showSettings,
  });

  if (reviewingOffer) {
    return null;
  }

  return (
    <Fragment>
      {showSettings && (
        <div className={buttonClasses}>
          <BetslipSettings
            handleSettings={handleSettings}
            setShowBetConfirmation={setShowBetConfirmation}
            showBetConfirmation={showBetConfirmation}
          />
        </div>
      )}
      {!showSettings &&
        <div className={buttonClasses}>
          <div className={styles.buttonAndLabel}>
            <Button
              className={styles.button}
              sizeType="regular"
              buttonType="primary"
              layout="circle"
              dataHook="betslip-cancelallbets"
              title="Betslip Settings"
              onClick={() => {
                handleSettings(!showSettings);
              }}
            >
              <IconSettings
                className={styles.rowIcon}
              />
            </Button>
            <span>Betslip Settings</span>
          </div>
          {(activeTab !== 'unmatched') && (
            <div className={styles.buttonAndLabel}>
              <Button
                className={styles['button--cancel']}
                sizeType="regular"
                layout="circle"
                loading={isCancelling}
                buttonType="primary"
                dataHook="betslip-cancelallbets"
                title="Remove all bets"
                onClick={(e) => {
                  if (activeTab === 'betslip') {
                    removeAllBets(e);
                  } else {
                    cancelAllBets();
                  }
                  track('Betslip', 'Clicked cancel all unsubmitted offers');
                }}
                disabled={!enabled}
              >
                <IconClose
                  className={styles.rowIcon}
                />
              </Button>
              <span>Cancel All</span>
            </div>
          )}
          {(activeTab !== 'unmatched') && (
            <div className={styles.buttonAndLabel}>
              <Button
                className={styles['button--submit']}
                sizeType="regular"
                layout="circle"
                buttonType="alternative"
                dataHook="betslip-placeallbets"
                title="Submit all bets"
                onClick={(e) => {
                  submitAllBets(e);
                  track('Betslip', 'Clicked submit all bets');
                }}
                disabled={submitAllDisabled}
              >
                <IconCheck
                  className={styles.rowIcon}
                />
              </Button>
              <span>Submit All</span>
            </div>
          )}
          {(activeTab === 'unmatched') && (
            <Fragment>
              <div className={styles.buttonAndLabel}>
                <Button
                  className={styles.button}
                  disabled={hasOfferSubmitted || !enabled}
                  sizeType="regular"
                  layout="circle"
                  buttonType="alternative"
                  dataHook="betslip-placeallbets"
                  title="Aggregation style"
                  onClick={() => {
                    handleAgregationRules();
                    track('Betslip', 'Clicked agregation rules');
                  }}
                >
                  {agregationRule || 'AVG'}
                </Button>
                <span>Aggregation Style</span>
              </div>
              <div className={styles.buttonAndLabel}>
                <Button
                  className={styles['button--cancel']}
                  sizeType="regular"
                  layout="circle"
                  loading={isCancelling}
                  buttonType="primary"
                  dataHook="betslip-cancelallbets"
                  title="Cancel all unmatched offers"
                  onClick={(e) => {
                    if (activeTab === 'betslip') {
                      removeAllBets(e);
                    } else {
                      cancelAllBets();
                    }
                    track('Betslip', 'Clicked Cancel All Unmatched Offers');
                  }}
                  disabled={!enabled || !(activeFilters && activeFilters.status !== 'matched' && isUnmatched)}
                >
                  <IconClose
                    className={styles.rowIcon}
                  />
                </Button>
                <span>Cancel Unmatched</span>
              </div>
            </Fragment>
          )}
          {confirmCancelAll && (
            <ConfirmAction
              action="cancel"
              onDismiss={() => setConfirmCancelAll(false)}
              onAccept={() => onCancelOffers(offers.map(offer => offer.id))}
              onUpdate={setCancelAllWarning}
              offersLength={offers.size}
            />
          )}
          {confirmSubmitAll && (
            <ConfirmAction
              action="submit"
              onDismiss={() => setConfirmSubmitAll(false)}
              onAccept={() => onSubmitOffers(offers)}
              onUpdate={setSubmitAllWarning}
              offersLength={offers.size}
            />
          )}
          {activeTab !== 'unmatched' ? (
            <div className={styles.buttonAndLabel}>
              <Button
                className={styles.button}
                sizeType="regular"
                buttonType="primary"
                layout="circle"
                onClick={(e) => {
                  convertSelections(e);
                  track('Accas', 'Click on Add to Multiple', {
                    description: `Added ${offers.size} selections`
                  });
                }}
                title={!multiplesAvailable ? 'Multiples are currently not available' : 'Convert to Multiple'}
                dataHook="convert-multiple"
              >
                <IconMultiples
                  className={styles.rowIcon}
                />
              </Button>
              <span>Convert to Multiple</span>
            </div>
          ) : null}
        </div>
      }
    </Fragment>
  );
};

NewButtons.propTypes = {
  onRemoveMultipleSelections: PropTypes.func,
  onRemoveOffers: PropTypes.func,
  onSubmitOffers: PropTypes.func,
  onCancelOffers: PropTypes.func,
  handleSettings: PropTypes.func,
  setSubmitAllWarning: PropTypes.func,
  setCancelAllWarning: PropTypes.func,
  setAggregationRule: PropTypes.func,
  config: PropTypes.object,
  aggregationRuleStored: PropTypes.string,
  showBetConfirmation: PropTypes.bool,
  showSettings: PropTypes.bool,
  setShowBetConfirmation: PropTypes.func,
  activeFilters: PropTypes.object,
  updateRightSidebar: PropTypes.func.isRequired,
  offers: PropTypes.instanceOf(List).isRequired,
  activeTab: PropTypes.string.isRequired,
  multiplesAvailable: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]).isRequired
};

export default NewButtons;
